/**
 * CPF Formatting Utilities
 * Handles formatting and validation of Brazilian CPF (Cadastro de Pessoas Físicas)
 */

import { hasMinimumRoleLevel } from '../config/roles';

/** Role level of síndico / condo_manager — the floor for seeing a real CPF. */
const MANAGER_LEVEL = 70;
/** Role level of porteiro / condo_staff — the floor for front-desk actions. */
const STAFF_LEVEL = 60;

/**
 * Formats a CPF string to the standard format: 000.000.000-00
 * @param value - The CPF value to format (with or without formatting)
 * @returns Formatted CPF string
 */
export const formatCPF = (value: string): string => {
  // Remove all non-digit characters
  const numbers = value.replace(/\D/g, '');

  // Limit to 11 digits
  const limitedNumbers = numbers.substring(0, 11);

  // Apply formatting
  if (limitedNumbers.length <= 3) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 6) {
    return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
  } else if (limitedNumbers.length <= 9) {
    return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
  } else {
    return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9, 11)}`;
  }
};

/**
 * Removes all formatting from a CPF string, keeping only digits
 * @param value - The formatted CPF
 * @returns CPF with only digits
 */
export const unformatCPF = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validates CPF format (basic check for 11 digits)
 * Note: This does not validate the verification digits
 * @param value - The CPF to validate
 * @returns true if the CPF has 11 digits
 */
export const isValidCPFLength = (value: string): boolean => {
  const numbers = unformatCPF(value);
  return numbers.length === 11;
};

/**
 * React onChange handler for CPF input fields
 * Automatically formats the CPF as the user types
 * @param setter - The state setter function
 * @returns onChange handler function
 */
export const handleCPFChange = (setter: (value: string) => void) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setter(formatted);
  };
};

/**
 * React onChange handler for CPF input in form objects
 * @param formData - Current form data object
 * @param setFormData - Form data setter function
 * @param fieldName - Name of the CPF field in the form (default: 'cpf')
 * @returns onChange handler function
 */
export const handleCPFChangeInForm = <T extends Record<string, any>>(
  formData: T,
  setFormData: (data: T) => void,
  fieldName: string = 'cpf'
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData({ ...formData, [fieldName]: formatted });
  };
};

/**
 * Who may see a person's real CPF — managers and above (síndico, supervisor, condo_manager,
 * every admin tier). Everyone below gets `***.xxx.xxx-**`.
 *
 * This is the ONE gate: view mode (CPFDisplay's eye toggle), the edit field, and the EDIT
 * permission itself (personModalRules.canEditCPF) are all built on it, so the answer here is
 * also the answer to "may I change this CPF".
 *
 * It used to be a hardcoded list of four admin role names, which left the síndico — who runs
 * the condomínio, whom the backend already lets PUT /people, and who may already replace a
 * person's identity photo — reading a masked value they could neither edit nor clear.
 * Expressed against ROLE_HIERARCHY so it moves with the rest of the app instead of drifting:
 * `administrador` (65) is deliberately BELOW síndico and still sees the mask.
 *
 * UI gate only. The backend enforces the real thing.
 *
 * @param userRoles - Array of user role strings
 * @returns true if user can view full CPF
 */
export const canViewFullCPF = (userRoles: string[]): boolean => {
  return hasMinimumRoleLevel(userRoles, MANAGER_LEVEL);
};

/**
 * Who may register a person WITHOUT a CPF — the "Permitir cadastro sem CPF" affordance on every
 * cadastro modal (Novo Morador, ResidentModal, VisitorRegistrationModal).
 *
 * Staff and above. Deliberately a LOWER bar than canViewFullCPF: reading a stored CPF is a
 * privacy question, while registering somebody who has no document on them is a front-desk one —
 * it is the porteiro who meets that person, and the backend keeps a placeholder CPF for exactly
 * this case.
 *
 * It replaces THREE hand-rolled role lists in two different shapes, which had drifted into the
 * worst of both: a porteiro could register a MORADOR with no CPF while the síndico could not, and
 * that same porteiro could not do it for a VISITANTE.
 *
 * @param userRoles - Array of user role strings (customer-scoped or realm)
 * @param isSuperAdmin - the context flag, true even when the role list comes back empty
 */
export const canRegisterWithoutCPF = (
  userRoles?: string[] | null,
  isSuperAdmin: boolean = false,
): boolean => {
  if (isSuperAdmin) return true;
  return hasMinimumRoleLevel(userRoles ?? [], STAFF_LEVEL);
};

/**
 * Mask CPF for non-admin users
 * Pattern: ***.xxx.xxx-** (show middle 6 digits, hide first 3 and last 2)
 * Example: 123.456.789-01 becomes ***.456.789-**
 *
 * @param cpf - The CPF to mask (formatted or unformatted)
 * @param userRoles - Array of user role strings
 * @returns Masked or full CPF based on user permissions
 */
export const maskCPF = (cpf: string | null | undefined, userRoles: string[]): string => {
  // Handle null/undefined
  if (!cpf) return '';

  // If user is admin, return formatted CPF
  if (canViewFullCPF(userRoles)) {
    return formatCPF(cpf);
  }

  // Get just the digits
  const digits = unformatCPF(cpf);

  // If less than 11 digits, return masked asterisks (malformed CPF)
  if (digits.length < 11) {
    return '***.***.**-**';
  }

  // Mask pattern: ***.xxx.xxx-**
  // Show digits 3-8 (middle 6 digits)
  const middle6 = digits.substring(3, 9); // indices 3,4,5,6,7,8
  return `***.${middle6.substring(0, 3)}.${middle6.substring(3)}-**`;
};

/**
 * Format and mask CPF for display
 * This is the main function to use for displaying CPF values
 *
 * @param cpf - The CPF to display
 * @param userRoles - Array of user role strings
 * @returns Formatted CPF (masked if non-admin), or '-' if no CPF
 */
export const displayCPF = (cpf: string | null | undefined, userRoles: string[]): string => {
  if (!cpf) return '-';
  return maskCPF(cpf, userRoles);
};
